/**
 * REPORTAR LO QUE VES (REPORTE-PANTALLAZO-T1, maqueta RcnJzrZAKLsfmnqpyCJh39, SI de Josse #43980).
 *
 * Un reporte es una nota con la etiqueta 'reporte': nexo-api 796 (8390ee3) la guarda y, si trae esa etiqueta, avisa a
 * Josse por WhatsApp con el tipo, la pantalla y la version (metadata.pantalla y metadata.version), sin datos de quien
 * reporta. El pantallazo va despues como adjunto de la nota. Sin DOM: lo prueba scripts/check-reportar.mjs.
 *
 * @module application/reportes/reporte
 */

/** Los tres tipos de la maqueta con la categoria que entiende la API (CreateNoteDto.Category). */
export const TIPOS = [
  { id: 'bug', texto: 'Algo está mal' },
  { id: 'improvement', texto: 'Una mejora' },
  { id: 'question', texto: 'Una duda' }
];

/** Lo que se tapa en el pantallazo antes de tomarlo: claves y datos de tarjeta. */
export const SENSIBLES = 'input[type="password"], input[autocomplete^="cc-"], input[autocomplete="one-time-code"], [data-sensible]';

const texto = (v) => (v === null || v === undefined ? '' : String(v).trim());

// El nombre de cada vista como lo dice la persona (la ruta del hash si no esta aqui).
const VISTAS = { presupuestos: 'Presupuestos', notes: 'Notas', dashboard: 'Inicio', auditoria: 'Auditoría', inventario: 'Inventario' };

/** La pantalla que va en el reporte: la vista del hash y, si la da quien abre, el detalle ('Armar presupuesto › Cubierta'). */
export function pantallaDe(hash, detalle = '') {
  const ruta = texto(hash).replace(/^#\/?/, '').split(/[/?]/)[0].toLowerCase();
  const vista = VISTAS[ruta] || (ruta ? ruta.charAt(0).toUpperCase() + ruta.slice(1) : 'Panel');
  const d = texto(detalle);
  return d ? `${vista} › ${d}` : vista;
}

/**
 * El cuerpo de POST /api/v1/notes (camelCase como CreateNoteDto) o las fallas. Title es requerido y de 1 a 200 letras;
 * la etiqueta 'reporte' es la que dispara el aviso de la API. Un error va con prioridad alta.
 * @returns {{ok:true, cuerpo:object} | {ok:false, fallas:string[]}}
 */
export function cuerpoDelReporte({ tipo, texto: escrito, pantalla, version, ruta, negocio }) {
  const t = texto(escrito);
  const fallas = [];
  if (!TIPOS.some((x) => x.id === tipo)) fallas.push('TIPO');
  if (!t) fallas.push('TEXTO');
  if (fallas.length) return { ok: false, fallas };
  const nombre = TIPOS.find((x) => x.id === tipo).texto;
  const corto = t.length > 120 ? t.slice(0, 118) + '…' : t;
  const cuerpo = {
    title: `${nombre}: ${corto}`.slice(0, 200),
    body: t,
    category: tipo,
    priority: tipo === 'bug' ? 'high' : 'medium',
    tags: ['reporte'],
    metadata: { pantalla: texto(pantalla) || 'Panel', version: texto(version), ruta: texto(ruta) }
  };
  if (texto(negocio)) cuerpo.businessId = texto(negocio);
  return { ok: true, cuerpo };
}

/** El id de la nota creada, en la respuesta del ApiBase ({ ok, data: { success, data: { id } } }) o ya leida. */
export function idDeLaNota(res) {
  const d = res && typeof res === 'object' && 'ok' in res ? res.data : res;
  const p = d && typeof d === 'object' && d.data && typeof d.data === 'object' ? d.data : d;
  return texto(p && p.id) || null;
}

/** Lo que dice la ventana despues de enviar: ok, sin pantallazo (la nota quedo) o no se envio (el texto sigue ahi). */
export function fraseDelEnvio({ nota, adjunto, conPantallazo }) {
  if (!nota || !nota.ok) {
    const c = nota && nota.error && nota.error.code;
    return { ok: false, frase: c === 'UNAUTHORIZED' ? 'Tu sesión venció: entra otra vez y vuelve a reportar.' : 'No se envió. Lo que escribiste sigue aquí: intenta de nuevo en un momento.' };
  }
  if (conPantallazo && (!adjunto || !adjunto.ok)) return { ok: true, frase: 'Enviado a Nexo, sin el pantallazo (no se pudo subir). Lo ves en Notas.' };
  return { ok: true, frase: conPantallazo ? 'Lo ves en Notas con su pantallazo. Te avisamos cuando esté resuelto.' : 'Lo ves en Notas. Te avisamos cuando esté resuelto.' };
}

/**
 * MAD-REPORTAR-SIEMPRE-VISIBLE-T1 (Ares #44163): el pantallazo se sube liviano. Ancho maximo 1600 px (se escala
 * parejo, nunca se agranda), JPEG a 0,8 y, si pasa de 300 KB, a 0,6. El primero pesaba 241.611 B en PNG a 2560x1346.
 */
export const PANTALLAZO = { anchoMaximo: 1600, metaBytes: 300000, calidades: [0.8, 0.6], tipo: 'image/jpeg' };

/** La medida a la que se sube un pantallazo de ancho x alto: igual si cabe, escalado parejo si es mas ancho. */
export function medidaDelPantallazo(ancho, alto, max = PANTALLAZO.anchoMaximo) {
  const a = Math.max(0, Math.round(Number(ancho) || 0));
  const h = Math.max(0, Math.round(Number(alto) || 0));
  if (!a || !h || a <= max) return { ancho: a, alto: h };
  return { ancho: max, alto: Math.max(1, Math.round(h * max / a)) };
}

/** La calidad que sigue: la primera, o la siguiente si el intento anterior (bytes) paso la meta; null si ya no hay. */
export function calidadSiguiente(bytesAnterior = null, intento = 0) {
  if (intento === 0) return PANTALLAZO.calidades[0];
  if (bytesAnterior !== null && bytesAnterior <= PANTALLAZO.metaBytes) return null;
  return PANTALLAZO.calidades[intento] ?? null;
}
